import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { GridColDef, useGridApiRef } from '@mui/x-data-grid-premium';
import StyledDataGrid from '../../components/DataGrid/DataGridStyles';
import {
  Settings as SettingsIcon,
  Save as SaveIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { FormControl, Select } from '@mui/material';
import { CustomButton } from '../../components/Infra/CustomButton/CustomButton';
import TextMatchingModal, {
  TextMatchingRule,
} from '../../components/Modal/TextMatchingModal';
import MultiColumnMatchingModal, {
  MultiColumnMatchingRule,
} from '../../components/Modal/MultiColumnMatchingModal';
import MathOperationModal, {
  MathOperationRule,
} from '../../components/Modal/MathOperationModal';
import DateFormatMatchingModal, {
  DateFormatRule,
} from '../../components/Modal/DateFormatMatchingModal';
import ResetConfigModal from '../../components/Modal/ResetConfigModal';
import PageHeader from '../../components/PageHeader/PageHeader';
import Toolbar from '../../components/Toolbar/Toolbar';
import { tableData } from '../../components/DataFeedConfiguration/DanelTransactionsConfig';
import { showSnackbar } from '../../context/Snackbar/SnackbarContext';
import { SnackbarType } from '../../context/Snackbar/Types';
import Actions from '../../store/Actions';
import { useNavigate } from 'react-router-dom';
import { useTableSearch } from '../../hooks/useTableSearch';

export interface FieldTransformation {
  type:
    | 'text_matching'
    | 'multi_column_matching'
    | 'math_operation'
    | 'date_format'
    | null;
  textMatching?: TextMatchingRule[];
  multiColumnMatching?: MultiColumnMatchingRule[];
  mathOperation?: MathOperationRule;
  dateFormat?: DateFormatRule;
}

export interface FieldMapping {
  column: string | null;
  additionalColumn?: string | null; // For multi-column fields like Deposit/Withdraw
  transformation?: FieldTransformation;
  autoDeductionRebate?: boolean; // For auto-determining Deduction/Rebate from Quantity sign
}

// DataGrid row interface
export interface ConfigurationRow {
  id: string;
  category: string;
  fieldName: string;
  columnMapping: string | null;
  transformationType?: string;
  hasTransformation: boolean;
  isConfigurable: boolean;
}

// Fields that should have configuration icons (based on your requirements)
const CONFIGURABLE_FIELDS = new Set([
  'Transaction date',
  'Settlement date',
  'Instrument Type',
  'Buy/Sell',
  'Commission',
  'Other Charges',
  'Buy Currency', // FX Spot currency field
  'Sell Currency', // FX Spot currency field
  'Currency', // Currency field in Deposits/Withdrawals, Transfer, Cash Adjustments
  'Settlement Account',
  'Deposit/Withdraw',
  'Fee Type',
  'Deduction/Rebate',
]);

const DanelConfigurationPage: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [headers, setHeaders] = useState<string[]>([]);
  const [mappings, setMappings] = useState<
    Record<string, Record<string, FieldMapping>>
  >({});
  const [loading, setLoading] = useState<boolean>(true);
  const [configurationRows, setConfigurationRows] = useState<
    ConfigurationRow[]
  >([]);

  // Filter configuration to only include fields that are currently defined in tableData
  const filterActiveConfiguration = (
    mappings: Record<string, Record<string, FieldMapping>>
  ): Record<string, Record<string, FieldMapping>> => {
    const filtered: Record<string, Record<string, FieldMapping>> = {};

    // Create a lookup of valid categories and their fields
    const validCategories = new Map<string, Set<string>>();
    tableData.forEach(category => {
      validCategories.set(category.category, new Set(category.headers));
    });

    Object.entries(mappings).forEach(([category, fields]) => {
      const validFields = validCategories.get(category);

      // Only process categories that exist in tableData
      if (validFields) {
        const filteredFields: Record<string, FieldMapping> = {};

        Object.entries(fields).forEach(([fieldName, mapping]) => {
          // Only include fields that are in the current tableData
          if (validFields.has(fieldName)) {
            // Clean the mapping - only include non-undefined values
            const cleanMapping: FieldMapping = {
              column: mapping.column,
            };

            if (mapping.additionalColumn !== undefined) {
              cleanMapping.additionalColumn = mapping.additionalColumn;
            }
            if (mapping.transformation !== undefined) {
              cleanMapping.transformation = mapping.transformation;
            }
            if (mapping.autoDeductionRebate !== undefined) {
              cleanMapping.autoDeductionRebate = mapping.autoDeductionRebate;
            }

            filteredFields[fieldName] = cleanMapping;
          }
        });

        // Only include the category if it has any fields
        if (Object.keys(filteredFields).length > 0) {
          filtered[category] = filteredFields;
        }
      }
    });

    return filtered;
  };

  const [activeModal, setActiveModal] = useState<{
    type:
      | 'text_matching'
      | 'multi_column_matching'
      | 'math_operation'
      | 'date_format'
      | null;
    category: string;
    header: string;
  }>({ type: null, category: '', header: '' });

  const [copyMenuAnchor, setCopyMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [copyTargetCategory, setCopyTargetCategory] = useState<string>('');

  // New state for reset table modal
  const [showResetModal, setShowResetModal] = useState(false);

  const apiRef = useGridApiRef();
  const navigate = useNavigate();

  // Convert mappings to DataGrid rows for current tab
  const getCurrentTabRows = useCallback((): ConfigurationRow[] => {
    if (currentTab >= tableData.length) return [];

    const category = tableData[currentTab];
    const categoryMappings = mappings[category.category] || {};

    return category.headers.map(header => {
      const mapping = categoryMappings[header];
      const hasTransformation = Boolean(mapping?.transformation?.type);

      return {
        id: `${category.category}-${header}`,
        category: category.category,
        fieldName: header,
        columnMapping: mapping?.column || null,
        transformationType: mapping?.transformation?.type || undefined,
        hasTransformation,
        isConfigurable: CONFIGURABLE_FIELDS.has(header),
      };
    });
  }, [currentTab, mappings]);

  // Setup search functionality
  const { filteredData, searchTerm, handleSearch } =
    useTableSearch<ConfigurationRow>(configurationRows);

  // Update configuration rows when mappings or current tab changes
  useEffect(() => {
    const rows = getCurrentTabRows();
    setConfigurationRows(rows);
  }, [mappings, currentTab, getCurrentTabRows]);

  useEffect(() => {
    const fetchHeadersAndConfiguration = async () => {
      try {
        setLoading(true);
        console.log('Fetching headers...');
        const headers = await Actions.getDanelExcelHeaders();
        console.log('Headers received:', headers);
        setHeaders(headers);

        // Try to load configuration from backend first
        try {
          console.log('Fetching configuration from backend...');
          const backendConfig = await Actions.getDanelConfiguration();

          if (
            backendConfig.status === 'success' &&
            backendConfig.config_headers
          ) {
            const { config_headers: field_mappings, transformations } =
              backendConfig;
            console.log('Backend configuration loaded:', {
              field_mappings,
              transformations,
            });

            // Transform backend format to frontend format
            const transformedMappings: Record<
              string,
              Record<string, FieldMapping>
            > = {};

            if (field_mappings) {
              Object.entries(field_mappings).forEach(
                ([category, fields]: [string, Record<string, unknown>]) => {
                  transformedMappings[category] = {};

                  if (fields && typeof fields === 'object') {
                    Object.entries(fields).forEach(
                      ([field, column]: [string, unknown]) => {
                        // Handle case where column might be an object with {column, transformation}
                        const columnValue =
                          typeof column === 'object' &&
                          column !== null &&
                          'column' in column
                            ? column.column
                            : column;

                        transformedMappings[category][field] = {
                          column: columnValue,
                          additionalColumn:
                            typeof column === 'object' &&
                            column !== null &&
                            'additionalColumn' in column
                              ? column.additionalColumn
                              : undefined,
                          transformation:
                            transformations?.[category]?.[field] || undefined,
                          autoDeductionRebate:
                            typeof column === 'object' &&
                            column !== null &&
                            'autoDeductionRebate' in column
                              ? column.autoDeductionRebate
                              : undefined,
                        };
                      }
                    );
                  }
                }
              );
            }

            // Filter to only include active fields before setting state
            const filteredMappings =
              filterActiveConfiguration(transformedMappings);
            setMappings(filteredMappings);
            console.log('Configuration loaded from backend successfully');
            console.log(
              'Filtered out stale fields, active mappings:',
              filteredMappings
            );
            return;
          }
        } catch (backendError) {
          console.log(
            'No backend configuration found, loading user preferences...',
            backendError
          );
        }

        // No fallback - use default mappings if backend config fails
        console.log('Using default configuration');
      } catch (error) {
        console.error('Failed to fetch headers:', error);
        showSnackbar({
          title: 'Error',
          type: SnackbarType.error,
          message: 'Failed to load configuration data',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchHeadersAndConfiguration();
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  // Export handler
  const handleExport = () => {
    if (apiRef.current) {
      apiRef.current.exportDataAsCsv({
        fileName: `danel-configuration-${
          tableData[currentTab]?.category || 'all'
        }.csv`,
      });
    }
  };

  // DataGrid columns configuration
  const columns: GridColDef[] = [
    {
      field: 'fieldName',
      headerName: 'Field Name',
      minWidth: 200,
    },
    {
      field: 'columnMapping',
      headerName: 'Column Mapping',
      width: 300,
      renderCell: params => {
        // Special handling for Deposit/Withdraw field in Deposits and Withdrawals category
        const isDepositWithdrawField =
          params.row.category === 'Deposits and Withdrawals' &&
          params.row.fieldName === 'Deposit/Withdraw';

        // Special handling for Fee Type field in Cash Adjustments category
        const isFeeTypeField =
          params.row.category === 'Cash Adjustments' &&
          params.row.fieldName === 'Fee Type';

        if (isDepositWithdrawField || isFeeTypeField) {
          const mapping = mappings[params.row.category]?.[params.row.fieldName];
          const primaryColumn = mapping?.column || '';
          const additionalColumn = mapping?.additionalColumn || '';

          return (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                width: '100%',
              }}
            >
              <FormControl size="small" sx={{ flex: 1, minWidth: 120 }}>
                <Select
                  value={primaryColumn}
                  onChange={e => {
                    handleColumnChange(
                      params.row.category,
                      params.row.fieldName,
                      e.target.value
                    );
                  }}
                  variant="standard"
                  displayEmpty
                  renderValue={selected => {
                    if (!selected) {
                      return <span style={{ color: '#999' }}>Column 1</span>;
                    }
                    return selected;
                  }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {(headers || []).map(header => (
                    <MenuItem key={header} value={header}>
                      {header}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="body2">+</Typography>
              <FormControl size="small" sx={{ flex: 1, minWidth: 120 }}>
                <Select
                  value={additionalColumn}
                  onChange={e => {
                    handleAdditionalColumnChange(
                      params.row.category,
                      params.row.fieldName,
                      e.target.value
                    );
                  }}
                  variant="standard"
                  displayEmpty
                  renderValue={selected => {
                    if (!selected) {
                      return <span style={{ color: '#999' }}>Column 2</span>;
                    }
                    return selected;
                  }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {(headers || []).map(header => (
                    <MenuItem key={header} value={header}>
                      {header}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          );
        }

        // Regular single column selector for all other fields
        // Check if this is Deduction/Rebate field and auto-mode is enabled for Quantity
        const isDeductionRebateField =
          params.row.category === 'Cash Adjustments' &&
          params.row.fieldName === 'Deduction/Rebate';
        const isAutoModeEnabled =
          mappings[params.row.category]?.['Quantity']?.autoDeductionRebate ||
          false;
        const isDisabled = isDeductionRebateField && isAutoModeEnabled;

        return (
          <FormControl fullWidth size="small">
            <Select
              value={params.value || ''}
              onChange={e => {
                const newValue = e.target.value;
                handleColumnChange(
                  params.row.category,
                  params.row.fieldName,
                  newValue
                );
              }}
              variant="standard"
              disabled={isDisabled}
              sx={{
                width: '100%',
                ...(isDisabled && {
                  backgroundColor: 'rgba(0, 0, 0, 0.06)',
                  '& .MuiSelect-select': {
                    color: 'text.disabled',
                  },
                }),
              }}
              displayEmpty
              renderValue={selected => {
                if (isDisabled) {
                  return (
                    <span style={{ color: '#999', fontStyle: 'italic' }}>
                      Auto-determined from Quantity
                    </span>
                  );
                }
                if (!selected) {
                  return <span style={{ color: '#999' }}>Select field</span>;
                }
                return selected;
              }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {(headers || []).map(header => (
                <MenuItem key={header} value={header}>
                  {header}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      },
    },
    {
      field: 'configuration',
      headerName: 'Configuration',
      width: 200,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: params => {
        // Special handling for Quantity field in Cash Adjustments
        const isQuantityField =
          params.row.category === 'Cash Adjustments' &&
          params.row.fieldName === 'Quantity';

        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              height: '100%',
              width: '100%',
            }}
          >
            {isQuantityField ? (
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={
                      mappings[params.row.category]?.[params.row.fieldName]
                        ?.autoDeductionRebate || false
                    }
                    onChange={e =>
                      handleAutoDeductionRebateToggle(
                        params.row.category,
                        params.row.fieldName,
                        e.target.checked
                      )
                    }
                  />
                }
                label="Auto Deduction/Rebate"
                labelPlacement="end"
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                  },
                  margin: 0,
                }}
              />
            ) : (
              params.row.isConfigurable && (
                <>
                  <IconButton
                    size="small"
                    onClick={() =>
                      openTransformationModal(
                        params.row.category,
                        params.row.fieldName
                      )
                    }
                    disabled={false}
                    color={params.row.hasTransformation ? 'primary' : 'default'}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <SettingsIcon fontSize="small" />
                  </IconButton>
                  <Typography variant="body2" color="text.secondary">
                    {getExpectedTransformationType(params.row.fieldName)}
                  </Typography>
                </>
              )
            )}
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: params => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            width: '100%',
          }}
        >
          <Tooltip title="Delete this field configuration">
            <span>
              <IconButton
                size="small"
                onClick={() =>
                  handleDeleteFieldConfig(
                    params.row.category,
                    params.row.fieldName
                  )
                }
                disabled={!params.row.columnMapping}
                color="error"
                sx={{
                  '&:hover': {
                    backgroundColor: 'error.lighter',
                  },
                  '&:disabled': {
                    color: 'action.disabled',
                  },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const handleAutoDeductionRebateToggle = (
    category: string,
    header: string,
    enabled: boolean
  ) => {
    console.log('🔄 Auto Deduction/Rebate toggle:', {
      category,
      header,
      enabled,
    });
    const currentMapping = mappings[category]?.[header] || { column: null };
    setMappings(prev => {
      const updated = {
        ...prev,
        [category]: {
          ...(prev[category] || {}),
          [header]: {
            ...currentMapping,
            autoDeductionRebate: enabled,
          },
        },
      };
      console.log(
        '🔄 Updated mappings with toggle:',
        updated[category][header]
      );
      return updated;
    });
  };

  const handleColumnChange = (
    category: string,
    header: string,
    column: string | number | null
  ) => {
    const currentMapping = mappings[category]?.[header] || { column: null };
    setMappings(prev => ({
      ...prev,
      [category]: {
        ...(prev[category] || {}),
        [header]: {
          ...currentMapping,
          column: column as string | null,
        },
      },
    }));
  };

  const handleAdditionalColumnChange = (
    category: string,
    header: string,
    column: string | number | null
  ) => {
    const currentMapping = mappings[category]?.[header] || { column: null };
    setMappings(prev => ({
      ...prev,
      [category]: {
        ...(prev[category] || {}),
        [header]: {
          ...currentMapping,
          additionalColumn: column as string | null,
        },
      },
    }));
  };

  const handleTransformationSave = (
    category: string,
    header: string,
    type:
      | 'text_matching'
      | 'multi_column_matching'
      | 'math_operation'
      | 'date_format',
    data: unknown
  ) => {
    console.log('🔍 handleTransformationSave CALLED:', {
      category,
      header,
      type,
      data,
    });

    const currentMapping = mappings[category]?.[header] || {
      column: null,
      additionalColumn: null,
    };
    let newTransformation: FieldTransformation;

    // If multi-column matching, use the data directly from modal (don't merge with existing)
    if (type === 'multi_column_matching') {
      newTransformation = {
        type,
        multiColumnMatching: data,
      } as FieldTransformation;
    } else {
      newTransformation = {
        type,
        ...(type === 'text_matching'
          ? { textMatching: data }
          : type === 'math_operation'
            ? { mathOperation: data }
            : { dateFormat: data }),
      } as FieldTransformation;
    }

    console.log(
      '🔍 handleTransformationSave - newTransformation:',
      newTransformation
    );
    console.log(
      '🔍 handleTransformationSave - currentMapping with columns:',
      currentMapping
    );

    // Build the new mappings object with the updated transformation
    setMappings(prev => {
      const updated = {
        ...prev,
        [category]: {
          ...(prev[category] || {}),
          [header]: {
            column: currentMapping.column,
            additionalColumn: currentMapping.additionalColumn,
            transformation: newTransformation,
            autoDeductionRebate: currentMapping.autoDeductionRebate,
          },
        },
      } as typeof prev;

      // Immediately persist to backend so modal save actually saves
      (async () => {
        try {
          // Filter to only include active fields before saving
          const filteredUpdated = filterActiveConfiguration(updated);
          const transformedMappings = Object.fromEntries(
            Object.entries(filteredUpdated).map(([cat, fields]) => [
              cat,
              Object.fromEntries(
                Object.entries(fields).map(([field, mapping]) => [
                  field,
                  {
                    column: mapping.column,
                    additionalColumn: mapping.additionalColumn,
                    transformation: mapping.transformation,
                    autoDeductionRebate: mapping.autoDeductionRebate,
                  },
                ])
              ),
            ])
          );

          console.log(
            '🔍 handleTransformationSave - About to save transformedMappings:',
            transformedMappings
          );
          console.log(
            '🔍 handleTransformationSave - Specific field being saved:',
            transformedMappings[category]?.[header]
          );
          console.log(
            '🔍 handleTransformationSave - Multi-column data:',
            JSON.stringify(
              transformedMappings[category]?.[header]?.transformation
                ?.multiColumnMatching,
              null,
              2
            )
          );

          try {
            await Actions.saveDanelMappings(transformedMappings);
            console.log(
              '🔍 handleTransformationSave - ✅ Save to danel_config completed successfully'
            );
          } catch (error) {
            console.error(
              '🔍 handleTransformationSave - ❌ Save to danel_config FAILED:',
              error
            );
            throw error; // Re-throw to trigger the catch block below
          }

          showSnackbar({
            title: 'Config Saved',
            message: `Transformation for ${header} saved`,
            type: SnackbarType.success,
          });
        } catch (e) {
          console.error(
            '🔍 handleTransformationSave - Auto-save transformation failed',
            e
          );
          showSnackbar({
            title: 'Save Failed',
            message: `Could not save transformation for ${header}. Please try again`,
            type: SnackbarType.error,
          });
        }
      })();

      return updated;
    });
  };

  const openTransformationModal = (category: string, header: string) => {
    // Check if this is the Deposit/Withdraw field with multi-column configuration
    if (
      category === 'Deposits and Withdrawals' &&
      header === 'Deposit/Withdraw'
    ) {
      const mapping = mappings[category]?.[header];
      if (mapping?.column && mapping?.additionalColumn) {
        // Use multi-column matching modal
        setActiveModal({ type: 'multi_column_matching', category, header });
        return;
      }
    }

    // Check if this is the Fee Type field with multi-column configuration
    if (category === 'Cash Adjustments' && header === 'Fee Type') {
      const mapping = mappings[category]?.[header];
      if (mapping?.column && mapping?.additionalColumn) {
        // Use multi-column matching modal
        setActiveModal({ type: 'multi_column_matching', category, header });
        return;
      }
    }

    // Determine internal transformation key for other fields
    let typeKey: FieldTransformation['type'] = 'text_matching';

    if (header.toLowerCase().includes('date') || header.includes('תאריך')) {
      typeKey = 'date_format';
    } else if (
      header.toLowerCase().includes('commission') ||
      header.toLowerCase().includes('charges')
    ) {
      typeKey = 'math_operation';
    }
    // Currency fields now use text_matching (default)

    setActiveModal({ type: typeKey, category, header });
  };

  const closeModal = () => {
    setActiveModal({ type: null, category: '', header: '' });
  };

  const getCurrentTransformationData = (
    category: string,
    header: string,
    type: string
  ) => {
    const transformation = mappings[category]?.[header]?.transformation;
    if (!transformation || transformation.type !== type) return undefined;

    switch (type) {
      case 'text_matching':
        return transformation.textMatching;
      case 'multi_column_matching':
        return transformation.multiColumnMatching;
      case 'math_operation':
        return transformation.mathOperation;
      case 'date_format':
        return transformation.dateFormat;
      default:
        return undefined;
    }
  };

  const getExpectedTransformationType = (header: string) => {
    // Date fields
    if (
      header.toLowerCase().includes('date') ||
      header.toLowerCase().includes('תאריך') ||
      header.includes('Date')
    ) {
      return 'Date Format';
    }
    // Commission and charges fields should use math operations
    else if (
      header.toLowerCase().includes('commission') ||
      header.toLowerCase().includes('charges') ||
      header.toLowerCase().includes('other charges') ||
      header.includes('עמלה') ||
      header.includes('עמלות')
    ) {
      return 'Math Operation';
    }
    // Currency fields use text matching
    else if (
      header === 'Currency' ||
      header === 'Buy Currency' ||
      header === 'Sell Currency'
    ) {
      return 'Text Matching';
    }
    // Settlement account should use text matching
    else if (
      header.toLowerCase().includes('settlement account') ||
      header.toLowerCase().includes('account')
    ) {
      return 'Text Matching';
    }
    // Default to text matching
    else {
      return 'Text Matching';
    }
  };

  const handleSaveConfiguration = async () => {
    // First filter to only include active fields defined in tableData
    const filteredMappings = filterActiveConfiguration(mappings);

    try {
      console.log('💾 Saving configuration...', mappings);
      console.log(
        '💾 Cash Adjustments Quantity mapping:',
        mappings['Cash Adjustments']?.['Quantity']
      );

      console.log(
        '💾 Filtered mappings (only active fields):',
        filteredMappings
      );

      // Transform mappings to the format expected by the backend
      const transformedMappings = Object.fromEntries(
        Object.entries(filteredMappings).map(([category, fields]) => [
          category,
          Object.fromEntries(
            Object.entries(fields).map(([field, mapping]) => [
              field,
              {
                column: mapping.column,
                additionalColumn: mapping.additionalColumn,
                transformation: mapping.transformation,
                autoDeductionRebate: mapping.autoDeductionRebate,
              },
            ])
          ),
        ])
      );

      // Save mappings to the backend via API call first
      try {
        console.log(
          '💾 Sending to backend - transformedMappings:',
          transformedMappings
        );
        console.log(
          '💾 Quantity field in backend payload:',
          transformedMappings['Cash Adjustments']?.['Quantity']
        );
        await Actions.saveDanelMappings(transformedMappings);
        console.log('✅ Configuration saved to danel_config successfully');
      } catch (error) {
        console.error('❌ Failed to save to danel_config:', error);
        throw error; // Don't continue if main save fails
      }

      // User preferences backup removed - using views mechanism instead

      const totalFields = Object.values(filteredMappings).reduce(
        (total, category) => total + Object.keys(category).length,
        0
      );
      const totalTransformations = Object.values(filteredMappings).reduce(
        (total, category) =>
          total +
          Object.values(category).filter(
            mapping => mapping.transformation?.type
          ).length,
        0
      );

      // 🔄 Refresh configuration from backend so local state is 100% in sync
      try {
        const backendConfig = await Actions.getDanelConfiguration();
        if (
          backendConfig.status === 'success' &&
          backendConfig.config_headers
        ) {
          const { config_headers: field_mappings, transformations } =
            backendConfig;
          const refreshed: Record<string, Record<string, FieldMapping>> = {};

          Object.entries(field_mappings).forEach(
            ([cat, fields]: [string, Record<string, unknown>]) => {
              refreshed[cat] = {};
              if (fields && typeof fields === 'object') {
                Object.entries(fields).forEach(
                  ([field, col]: [string, unknown]) => {
                    // Handle case where col might be an object with {column, transformation}
                    const columnValue =
                      typeof col === 'object' && col !== null && 'column' in col
                        ? col.column
                        : col;

                    refreshed[cat][field] = {
                      column: columnValue,
                      additionalColumn:
                        typeof col === 'object' &&
                        col !== null &&
                        'additionalColumn' in col
                          ? col.additionalColumn
                          : undefined,
                      transformation:
                        transformations?.[cat]?.[field] || undefined,
                      autoDeductionRebate:
                        typeof col === 'object' &&
                        col !== null &&
                        'autoDeductionRebate' in col
                          ? col.autoDeductionRebate
                          : undefined,
                    };
                  }
                );
              }
            }
          );

          setMappings(refreshed);
        }
      } catch (refreshErr) {
        console.error('Could not refresh config after save', refreshErr);
      }

      showSnackbar({
        title: 'Configuration Saved',
        type: SnackbarType.success,
        message: `Transaction type configuration saved successfully (${totalFields} fields, ${totalTransformations} transformations)`,
      });
    } catch (error) {
      console.error('Error saving config:', error);

      showSnackbar({
        title: 'Save Failed',
        type: SnackbarType.error,
        message: 'Failed to save transaction type configuration',
      });
    }
  };

  const handleCopyFromClick = (
    event: React.MouseEvent<HTMLElement>,
    targetCategory: string
  ) => {
    setCopyMenuAnchor(event.currentTarget);
    setCopyTargetCategory(targetCategory);
  };

  const handleCopyFromClose = () => {
    setCopyMenuAnchor(null);
    setCopyTargetCategory('');
  };

  const handleCopyConfiguration = (sourceCategory: string) => {
    if (!copyTargetCategory || sourceCategory === copyTargetCategory) {
      handleCopyFromClose();
      return;
    }

    const sourceConfig = mappings[sourceCategory] || {};

    setMappings(prev => ({
      ...prev,
      [copyTargetCategory]: {
        ...sourceConfig,
      },
    }));

    showSnackbar({
      title: 'Configuration Copied',
      type: SnackbarType.success,
      message: `Configuration copied from ${sourceCategory} to ${copyTargetCategory}`,
    });

    handleCopyFromClose();
  };

  // Handler for deleting individual field configurations
  const handleDeleteFieldConfig = async (category: string, header: string) => {
    try {
      console.log(
        '🗑️ DELETE FIELD: Starting deletion for',
        category,
        '->',
        header
      );

      // Update local state first
      setMappings(prev => {
        const newMappings = { ...prev };
        if (newMappings[category] && newMappings[category][header]) {
          delete newMappings[category][header];
          // If category becomes empty, remove it entirely
          if (Object.keys(newMappings[category]).length === 0) {
            delete newMappings[category];
          }
        }
        console.log(
          '🗑️ DELETE FIELD: Updated local state, new mappings:',
          newMappings
        );
        return newMappings;
      });

      // Save the updated configuration to backend immediately
      const updatedMappings = { ...mappings };
      if (updatedMappings[category] && updatedMappings[category][header]) {
        delete updatedMappings[category][header];
        if (Object.keys(updatedMappings[category]).length === 0) {
          delete updatedMappings[category];
        }
      }

      // Filter to only include active fields before saving
      const filteredUpdatedMappings =
        filterActiveConfiguration(updatedMappings);

      // Transform mappings to the format expected by the backend
      const transformedMappings = Object.fromEntries(
        Object.entries(filteredUpdatedMappings).map(([cat, fields]) => [
          cat,
          Object.fromEntries(
            Object.entries(fields).map(([field, mapping]) => [
              field,
              {
                column: mapping.column,
                additionalColumn: mapping.additionalColumn,
                transformation: mapping.transformation,
                autoDeductionRebate: mapping.autoDeductionRebate,
              },
            ])
          ),
        ])
      );

      console.log('🗑️ DELETE FIELD: Saving to backend:', transformedMappings);
      await Actions.saveDanelMappings(transformedMappings);
      console.log('🗑️ DELETE FIELD: Backend save successful');

      showSnackbar({
        title: 'Field Configuration Deleted',
        type: SnackbarType.success,
        message: `Configuration for "${header}" has been permanently removed from ${category}`,
      });
    } catch (error) {
      console.error('🗑️ DELETE FIELD: Error deleting field config:', error);
      showSnackbar({
        title: 'Delete Failed',
        type: SnackbarType.error,
        message: `Failed to delete configuration for "${header}"`,
      });
    }
  };

  // New handlers for reset table functionality
  const handleResetTableClick = (tableName: string) => {
    setResetTargetTable(tableName);
    setShowResetModal(true);
  };

  const handleResetTableConfirm = async (tableName: string) => {
    try {
      console.log('🔴 RESET TABLE: Starting reset for table:', tableName);

      // Call backend API to reset the table configuration
      console.log('🔴 RESET TABLE: Calling backend API...');
      await Actions.resetTableConfiguration(tableName);
      console.log('🔴 RESET TABLE: Backend API call successful');

      // Remove the table configuration from local state
      setMappings(prev => {
        const newMappings = { ...prev };
        delete newMappings[tableName];
        console.log(
          '🔴 RESET TABLE: Updated local state, new mappings:',
          newMappings
        );
        return newMappings;
      });

      // Refresh configuration from backend to ensure consistency
      try {
        console.log('🔴 RESET TABLE: Refreshing configuration from backend...');
        const backendConfig = await Actions.getDanelConfiguration();
        console.log('🔴 RESET TABLE: Backend config response:', backendConfig);

        if (
          backendConfig.status === 'success' &&
          backendConfig.config_headers
        ) {
          const { config_headers: field_mappings, transformations } =
            backendConfig;
          const transformedMappings: Record<
            string,
            Record<string, FieldMapping>
          > = {};

          if (field_mappings) {
            Object.entries(field_mappings).forEach(
              ([category, fields]: [string, Record<string, unknown>]) => {
                transformedMappings[category] = {};
                if (fields && typeof fields === 'object') {
                  Object.entries(fields).forEach(
                    ([field, column]: [string, unknown]) => {
                      // Handle case where column might be an object with {column, transformation}
                      const columnValue =
                        typeof column === 'object' &&
                        column !== null &&
                        'column' in column
                          ? column.column
                          : column;

                      transformedMappings[category][field] = {
                        column: columnValue,
                        additionalColumn:
                          typeof column === 'object' &&
                          column !== null &&
                          'additionalColumn' in column
                            ? column.additionalColumn
                            : undefined,
                        transformation:
                          transformations?.[category]?.[field] || undefined,
                        autoDeductionRebate:
                          typeof column === 'object' &&
                          column !== null &&
                          'autoDeductionRebate' in column
                            ? column.autoDeductionRebate
                            : undefined,
                      };
                    }
                  );
                }
              }
            );
          }
          console.log(
            '🔴 RESET TABLE: Setting refreshed mappings:',
            transformedMappings
          );
          setMappings(transformedMappings);
        }
      } catch (refreshError) {
        console.error(
          '🔴 RESET TABLE: Could not refresh configuration from backend:',
          refreshError
        );
      }

      showSnackbar({
        title: 'Table Reset Complete',
        type: SnackbarType.success,
        message: `${tableName} configuration has been permanently reset and removed from database`,
      });
    } catch (error) {
      console.error('🔴 RESET TABLE: Error resetting table config:', error);
      showSnackbar({
        title: 'Reset Failed',
        type: SnackbarType.error,
        message: `Failed to reset ${tableName} configuration: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      });
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <PageHeader
          title="Transaction Type Configuration"
          subtitle="Configure field mappings and transformations for different instrument types"
        />
        <CustomButton variant="text" onClick={() => navigate('/danel')}>
          Back to Data Feed
        </CustomButton>
      </Box>

      <Paper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          flex: 1,
          minHeight: 0,
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            aria-label="instrument type tabs"
          >
            {tableData.map(category => (
              <Tab key={category.category} label={category.category} />
            ))}
          </Tabs>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            paddingX: 2,
          }}
        >
          <Toolbar
            onSearch={handleSearch}
            placeholder={`Search ${
              tableData[currentTab]?.category || ''
            } configuration...`}
            searchTerm={searchTerm}
            disabled={loading}
            onExport={handleExport}
            buttons={[
              {
                variant: 'text',
                label: 'Copy From',
                color: 'secondary',
                icon: <CopyIcon />,
                onClick: e =>
                  handleCopyFromClick(e, tableData[currentTab]?.category || ''),
                disabled: loading,
              },
              {
                variant: 'text',
                label: 'Reset Table',
                icon: <DeleteIcon />,
                onClick: () =>
                  handleResetTableClick(tableData[currentTab]?.category || ''),
                disabled: loading,
                color: 'error',
              },
              {
                variant: 'contained',
                label: 'Save Configuration',
                icon: <SaveIcon />,
                onClick: handleSaveConfiguration,
                disabled: loading,
              },
            ]}
          />
        </Box>

        <Box sx={{ flex: 1, minHeight: 400, paddingX: 2, paddingBottom: 2 }}>
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <StyledDataGrid
              apiRef={apiRef}
              rows={filteredData}
              columns={columns}
              disableRowSelectionOnClick
              hideFooterSelectedRowCount
              disableColumnFilter
              disableColumnMenu
            />
          )}
        </Box>
      </Paper>

      {/* Transformation Modals */}
      <TextMatchingModal
        open={activeModal.type === 'text_matching'}
        onClose={closeModal}
        onSave={rules =>
          handleTransformationSave(
            activeModal.category,
            activeModal.header,
            'text_matching',
            rules
          )
        }
        initialRules={
          (getCurrentTransformationData(
            activeModal.category,
            activeModal.header,
            'text_matching'
          ) as TextMatchingRule[]) || []
        }
        fieldName={activeModal.header}
        category={activeModal.category}
      />

      <MultiColumnMatchingModal
        open={activeModal.type === 'multi_column_matching'}
        onClose={closeModal}
        onSave={rules =>
          handleTransformationSave(
            activeModal.category,
            activeModal.header,
            'multi_column_matching',
            rules
          )
        }
        initialRules={
          (getCurrentTransformationData(
            activeModal.category,
            activeModal.header,
            'multi_column_matching'
          ) as MultiColumnMatchingRule[]) || []
        }
        fieldName={activeModal.header}
        category={activeModal.category}
        column1Name={
          mappings[activeModal.category]?.[activeModal.header]?.column ||
          'Column 1'
        }
        column2Name={
          mappings[activeModal.category]?.[activeModal.header]
            ?.additionalColumn || 'Column 2'
        }
      />

      <MathOperationModal
        open={activeModal.type === 'math_operation'}
        onClose={closeModal}
        onSave={rule =>
          handleTransformationSave(
            activeModal.category,
            activeModal.header,
            'math_operation',
            rule
          )
        }
        initialRule={
          getCurrentTransformationData(
            activeModal.category,
            activeModal.header,
            'math_operation'
          ) as MathOperationRule
        }
        fieldName={activeModal.header}
        availableColumns={headers || []}
        defaultColumn={
          mappings[activeModal.category]?.[activeModal.header]?.column || null
        }
      />

      <DateFormatMatchingModal
        open={activeModal.type === 'date_format'}
        onClose={closeModal}
        onSave={rule =>
          handleTransformationSave(
            activeModal.category,
            activeModal.header,
            'date_format',
            rule
          )
        }
        initialRule={
          getCurrentTransformationData(
            activeModal.category,
            activeModal.header,
            'date_format'
          ) as DateFormatRule
        }
        fieldName={activeModal.header}
      />

      {/* Reset Table Modal */}
      <ResetConfigModal
        open={showResetModal}
        onClose={() => setShowResetModal(false)}
        onReset={handleResetTableConfirm}
        availableTables={tableData.map(t => t.category)}
      />

      {/* Copy Configuration Menu */}
      <Menu
        anchorEl={copyMenuAnchor}
        open={Boolean(copyMenuAnchor)}
        onClose={handleCopyFromClose}
        PaperProps={{
          sx: { minWidth: 200 },
        }}
      >
        <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" color="text.secondary">
            Copy configuration from:
          </Typography>
        </Box>
        {tableData
          .filter(category => category.category !== copyTargetCategory)
          .map(category => (
            <MenuItem
              key={category.category}
              onClick={() => handleCopyConfiguration(category.category)}
              disabled={
                !mappings[category.category] ||
                Object.keys(mappings[category.category] || {}).length === 0
              }
            >
              <ListItemIcon>
                <CopyIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={category.category}
                secondary={
                  mappings[category.category] &&
                  Object.keys(mappings[category.category]).length > 0
                    ? `${
                        Object.keys(mappings[category.category]).length
                      } fields configured`
                    : 'No configuration'
                }
              />
            </MenuItem>
          ))}
      </Menu>
    </Box>
  );
};

export default DanelConfigurationPage;
